import type { Editor as EditorType } from 'tinymce';
import { Editor } from '@tinymce/tinymce-react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect } from 'react';
import useOutsideClick from '~/components/custom-hooks/use-outside-click';
import { APP_CONSTANTS } from '~/common/explicit-exports';
import { TEXT_EDITOR_MODES } from '../../constants';
import { versionHistoryPlugin } from '../../utils';
import { EditorVersionModal } from '../EditorVersionModal';
import { RichTextContent } from '../RichTextContent';
import {
  editorStyles,
  StyledDisabledWrapper,
  StyledEditorWrapper,
  StyledEditorLoadingState,
  ToolbarStyles,
} from './TextEditor.styles';

interface BlobInfo {
  id: () => string;
  name: () => string;
  filename: () => string;
  blob: () => Blob;
  base64: () => string;
  blobUri: () => string;
  uri: () => string | undefined;
}

export type TextEditorProps = {
  id?: string;
  mode?: keyof typeof TEXT_EDITOR_MODES;
  autoFocus?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  defaultValue?: string;
  isDisabled?: boolean;
  imagesUploadUrl?: string;
  contentStyle?: string;
  imagesUploadHandler?: ((blobInfo: BlobInfo, progress: (percent: number) => void) => Promise<string>) | null;
  setup?: (editor: EditorType) => void;
  showBorder?: boolean;
  hideBackground?: boolean;
  onBlur?: () => void;
  label?: string;
};

export const TextEditor = forwardRef(
  (
    {
      autoFocus = false,
      contentStyle = editorStyles,
      defaultValue,
      id,
      imagesUploadHandler = null,
      imagesUploadUrl: imageUploadUrl,
      isDisabled = false,
      mode,
      onChange,
      placeholder = '',
      setup,
      value,
      showBorder = false,
      hideBackground = false,
      onBlur,
    }: TextEditorProps,
    ref,
  ) => {
    const [isShowingVersions, setIsShowingVersions] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<EditorType | null>(null);

    const { init, enableVersioning, ...rest } = TEXT_EDITOR_MODES[mode] ?? {};

    useImperativeHandle(ref, () => editorRef.current, []);

    useEffect(() => {
      if (shouldRender) return undefined;

      // If IntersectionObserver is not supported, render immediately
      if (typeof IntersectionObserver === 'undefined') {
        setShouldRender(true);
        return undefined;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldRender(true);
            observer.disconnect();
          }
        },
        {
          threshold: 0,
          rootMargin: '0px 0px 1000px 0px',
        },
      );

      if (wrapperRef.current) {
        observer.observe(wrapperRef.current);
      }

      return () => observer.disconnect();
    }, [shouldRender]);

    const handleRestore = useCallback(
      (version) => {
        editorRef.current?.setContent(version);
      },
      [editorRef],
    );

    const preprocessPastedContent = useCallback((editor: EditorType) => {
      editor.on('PastePreProcess', (e) => {
        e.content = e.content.replace(
          /(<li[^>]*style=")([^"]*?)(white-space:\s*(pre|pre-wrap);?)([^"]*?)"/gi,
          '$1$2$5"',
        );
      });
    }, []);

    const handleSetup = useCallback(
      (editor: EditorType) => {
        if (enableVersioning) {
          versionHistoryPlugin(editor, () => setIsShowingVersions(true));
        }
        preprocessPastedContent(editor);
      },
      [enableVersioning, preprocessPastedContent],
    );

    useOutsideClick(
      wrapperRef,
      useCallback(() => {
        if (editorRef?.current?.queryCommandState('ToggleToolbarDrawer')) {
          const focusedElement = document.activeElement as HTMLElement;
          editorRef.current?.execCommand('ToggleToolbarDrawer', false);
          focusedElement?.focus();
        }
      }, [editorRef]),
    );

    const handleEditorChange = useCallback(
      (val) => {
        if (hasUserInteracted) {
          onChange?.(val);
        }
      },
      [hasUserInteracted, onChange],
    );

    const createBatchFontImport = (fonts: string[], weights = '100;200;300;400;500;600;700;800;900'): string => {
      const fontQuery = fonts.map((font) => `family=${font.replace(/ /g, '+')}:wght@${weights}`).join('&');
      return `@import url('https://fonts.googleapis.com/css2?${fontQuery}&display=swap');`;
    };

    const generateFontImports = (): string => {
      const fonts = APP_CONSTANTS.GOOGLE_FONTS || [];
      const batchSize = 10;
      const batches = [];

      for (let i = 0; i < fonts.length; i += batchSize) {
        const batch = fonts.slice(i, i + batchSize);
        batches.push(createBatchFontImport(batch));
      }

      return batches.join('\n');
    };

    const getContentStyle = (): string => {
      const fontImports = generateFontImports();
      return `${fontImports}
              ${contentStyle}`;
    };

    return (
      <>
        {!isDisabled && (
          <StyledEditorWrapper ref={wrapperRef} $showBorder={showBorder} $hideBackground={hideBackground}>
            {shouldRender ? (
              <Editor
                id={id}
                tinymceScriptSrc={window.tinymceURL}
                onInit={(_, editor) => {
                  if (editorRef) {
                    // eslint-disable-next-line no-param-reassign
                    editorRef.current = editor;
                  }

                  // Don't allow editor to be saved until user has interacted with it
                  // Avoids unneeded saves when HTML is reformatted by TinyMCE
                  editor.on('keydown keyup mousedown mouseup input drag paste click ExecCommand', (e) => {
                    if (e.command === 'mceFocus' || hasUserInteracted) {
                      return;
                    }

                    setTimeout(() => setHasUserInteracted(true));
                  });

                  return editor;
                }}
                init={{
                  auto_focus: autoFocus ? 'true' : 'false',
                  placeholder,
                  content_style: getContentStyle(),
                  content_css: [],
                  images_upload_url: imageUploadUrl,
                  relative_urls: false,
                  link_assume_external_targets: 'https',
                  remove_script_host: false,
                  convert_urls: true,
                  valid_children: '+body[style]',
                  setup: setup || handleSetup,
                  images_upload_handler: imagesUploadHandler,
                  ...init,
                }}
                initialValue={defaultValue}
                onEditorChange={handleEditorChange}
                value={value}
                onBlur={onBlur}
                {...rest}
              />
            ) : (
              <StyledEditorLoadingState>Loading...</StyledEditorLoadingState>
            )}
          </StyledEditorWrapper>
        )}
        {isDisabled && !!value.trim() && (
          <StyledDisabledWrapper>
            <RichTextContent>{value}</RichTextContent>
          </StyledDisabledWrapper>
        )}
        {enableVersioning && (
          <EditorVersionModal
            id={id}
            isOpen={isShowingVersions}
            onSave={handleRestore}
            onClose={() => setIsShowingVersions(false)}
          />
        )}
        <ToolbarStyles />
      </>
    );
  },
);
